import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from 'lucide-react';
import { shipments } from '../../data/shipments';
import { Pagination, Table, TD, TH, THead, TR } from '../ui/Table';
import { SearchInput } from '../ui/SearchInput';
import { Select } from '../ui/Select';
import { StatusPill } from '../ui/StatusPill';
import { claimStatusOptions } from '../../data/claims';
import { Claim } from '../../types';
import { usePagination } from '../../hooks/usePagination';
function ShipmentLink({
  id
}: {
  id: string;
}) {
  const shipment = shipments.find(s => s.id === id);
  const content = <>
      {id}
      <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-41" data-mp="fugqilqwimoy" />
    </>;
  if (!shipment) {
    return <span className="inline-flex items-center gap-0.5 text-brand-600" data-id="element-42" data-mp="kp4gkp1jzp5n">{content}</span>;
  }
  return <Link to={shipment.status === 'Shipped' ? `/shipments/${shipment.uuid}` : `/shipments/${shipment.uuid}/edit`} onClick={e => e.stopPropagation()} className="inline-flex items-center gap-0.5 text-brand-600 hover:underline" data-id="element-43" data-mp="3k8f6hyb4lota">
      {content}
    </Link>;
}
export function ClaimsTable({
  claims
}: {
  claims: Claim[];
}) {
  const [status, setStatus] = useState('All Statuses');
  const [query, setQuery] = useState('');
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return claims.filter(claim => {
      if (status !== 'All Statuses' && claim.status !== status) return false;
      if (!q) return true;
      return `${claim.serialNumber} ${claim.assetTagId} ${claim.affectedDevice} ${claim.studentName}`.toLowerCase().includes(q);
    });
  }, [claims, status, query]);
  const {
    page,
    setPage,
    totalPages,
    pageItems
  } = usePagination(rows, 25);
  return <div className="flex flex-1 flex-col" data-id="element-44" data-mp="1u1dkakysun0x">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3" data-id="element-45" data-mp="1u02i2pl05nbx">
        <Select options={claimStatusOptions} value={status} placeholder="All Statuses" onChange={setStatus} clearable onClear={() => setStatus('All Statuses')} width={230} data-id="element-46" data-mp="1p32c0eo5kkkt" />
        <SearchInput value={query} onChange={setQuery} placeholder="Search devices by IMEI/SN or asset tag ID" className="w-[260px]" data-id="element-47" data-mp="39jteifk2xxk7" />
      </div>

      <Table minWidth={1240} data-id="element-48" data-mp="6332nk1tgqal">
        <THead data-id="element-49" data-mp="62t9jnlh630o">
          <tr data-id="element-50" data-mp="64e59gjizs04">
            <TH data-id="element-51" data-mp="63mc0cdei6d0">Affected Device</TH>
            <TH data-id="element-52" data-mp="b25ug2ssd00m">Serial Number</TH>
            <TH data-id="element-53" data-mp="b14kk1niajkh">Filing Date</TH>
            <TH data-id="element-54" data-mp="b1ebpskaewrr">School</TH>
            <TH data-id="element-55" data-mp="3sab2y7xvq6zr">Student Name</TH>
            <TH data-id="element-56" data-mp="3sb2kwfyk067k">Asset Tag ID</TH>
            <TH data-id="element-57" data-mp="3s9rmywrshsod">Current Status</TH>
            <TH data-id="element-58" data-mp="13ztpoiptzxc">Inbound Tracking</TH>
            <TH data-id="element-59" data-mp="12yk7ohcllhq">Tracking Number</TH>
          </tr>
        </THead>
        <tbody data-id="element-60" data-mp="2gv4bgazgri5t">
          {pageItems.length === 0 && <TR data-id="element-61" data-mp="2gtku0h22n12g">
              <TD colSpan={9} className="py-10 text-center text-ink-500" data-id="element-62" data-mp="2gx8n5f3ltlgn">
                No claims found
              </TD>
            </TR>}
          {pageItems.map(claim => <TR key={claim.id} data-id="element-63" data-mp="13yow0lxrknlj">
              <TD data-id="element-64" data-mp="yxrdu9cr7tk1">{claim.affectedDevice}</TD>
              <TD data-id="element-65" data-mp="ywzimzn2jxua">{claim.serialNumber}</TD>
              <TD data-id="element-66" data-mp="1cxa941kgvaex">{claim.filingDate}</TD>
              <TD data-id="element-67" data-mp="1cw8zm1j3mvzb">{claim.schoolName}</TD>
              <TD data-id="element-68" data-mp="1cv65zlue6ffp">{claim.studentName}</TD>
              <TD data-id="element-69" data-mp="18vfaj4tx7b4u">{claim.assetTagId}</TD>
              <TD data-id="element-70" data-mp="18wgll9axrxmk">
                <StatusPill status={claim.status} data-id="element-71" data-mp="18tuhjjn98093" />
              </TD>
              <TD data-id="element-72" data-mp="l6m94suj4b2u">
                {claim.inboundTracking ? <ShipmentLink id={claim.inboundTracking} data-id="element-73" data-mp="g7svn0f8sk2o" /> : '-'}
              </TD>
              <TD data-id="element-74" data-mp="tzpjutdsi9ts">{claim.trackingNumber ?? '-'}</TD>
            </TR>)}
        </tbody>
      </Table>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} data-id="element-75" data-mp="p5lcwa4iy1sp" />
    </div>;
}